// El JWT de sesión de la app ahora EXPIRA (JWT_TTL_HOURS en app.py, 12 h
// por defecto) y su firma se verifica de verdad (JWT_SIGNING_SECRET), así
// que un 401 ya no es un caso imposible: significa "token ausente,
// expirado o inválido". Antes el token era eterno y ningún fetch
// contemplaba el 401; ahora se borra el token y se relanza el login de
// Entra ID, guardando la ruta actual para volver a ella (mismo mecanismo
// postLoginRedirect que usa la página compartida /artifact/<id>).
//
// Devuelve true si ya se disparó el re-login: el caller debe cortar.
const reloginOn401 = (response: Response) => {
  if (response.status !== 401) return false;
  try {
    localStorage.removeItem('jwt');
    // Sólo la ruta (sin query): el ?code del login es de un solo uso y
    // volver a él provocaría un ciclo de redirecciones.
    const here = window.location.pathname + window.location.hash;
    if (here && here !== '/') {
      localStorage.setItem('postLoginRedirect', here);
    }
  } catch (e) {
    // localStorage bloqueado: se manda al login igual.
  }
  window.location.href = '/.auth/login/aad/callback';
  return true;
};

const fetchToken = async (jwt: string) => {

  const whereStartChat = localStorage.getItem('whereStartChat');
  if (whereStartChat === null || whereStartChat === 'newChat') {
    const response = await fetch(
      "/getToken",
      {
        method: "get",
        headers: {
          "Content-Type": "application/json",
          "Authorization": jwt
        }
      }
    );

    if (!response.ok) {
      reloginOn401(response);
      throw new Error(
        `Direct Line token API call failed with status ${response.status}`
      );
    }

    const responseJson = await response.json();

    return responseJson
  } else {
    return localStorage.getItem('lastToken');
  }
}

const fetchTokenFromTeams = async (token: string) => {
  const response = await fetch(
    "/getTeamsToken?token=" + token,
    {
      method: "get",
      headers: {
        "Content-Type": "application/json"
      }
    });

  if (!response.ok) {
    throw new Error(
      `Direct Line token API call failed with status ${response.status}`
    );
  }

  const responseJson = await response.text();

  return responseJson
}

const fetchTokenFromAAD = async (code: string) => {
  try{
    const response = await fetch(
      "/getTokenFromAAD?code=" + code,
      {
        method: "get",
        headers: {
          "Content-Type": "application/json"
        }
      });
  
    if (!response.ok) {
      throw new Error(
        `Direct Line token API call failed with status ${response.status}`
      );
    }
  
    const responseJson = await response.text();
  
    return responseJson
  } catch (error) {
    console.error('Fetching token from AAD error:', error);
    throw error;
  }
}

const fetchConversationsIds = async (jwt: string) => {
  const response = await fetch(
    "/conversationsIds",
    {
      method: "get",
      headers: {
        "Content-Type": "application/json",
        "Authorization": jwt
      }
    });

  if (!response.ok) {
    reloginOn401(response);
    throw new Error(
      `Direct Line token API call failed with status ${response.status}`
    );
  }

  const responseJson = await response.json();

  return responseJson;
}

const fetchConversationHistory = async (conversationId: string, jwt: string) => {
  const response = await fetch(
    "/conversationHistory/" + conversationId,
    {
      method: "get",
      headers: {
        "Content-Type": "application/json",
        "Authorization": jwt
      }
    });

  if (!response.ok) {
    reloginOn401(response);
    throw new Error(
      `Direct Line token API call failed with status ${response.status}`
    );
  }

  const responseJson = await response.json();

  return responseJson;
}

const fetchPromptsTotry = async (jwt: string, promptType: string) => {
  const response = await fetch(
    "/getPromptsToTry/" + promptType,
    {
      method: "get",
      headers: {
        "Content-Type": "application/json",
        "Authorization": jwt
      }
    });

  if (!response.ok) {
    reloginOn401(response);
    throw new Error(
      `API call failed with status ${response.status}`
    );
  }

  const responseJson = await response.json();

  return responseJson;
}

const fetchPromptsHistory = async (jwt: string) => {
  const response = await fetch(
    "/prompsHistory",
    {
      method: "get",
      headers: {
        "Content-Type": "application/json",
        "Authorization": jwt
      }
    });

  if (!response.ok) {
    reloginOn401(response);
    throw new Error(
      `Direct Line token API call failed with status ${response.status}`
    );
  }

  const responseJson = await response.json();

  return responseJson;
}

const fetchFavorites = async (jwt: string) => {
  const response = await fetch("/api/favorites", {
    method: "get",
    headers: { "Content-Type": "application/json", "Authorization": jwt }
  });
  if (!response.ok) {
    reloginOn401(response);
    throw new Error(`Failed with status ${response.status}`);
  }
  return response.json();
};

const saveFavorite = async (jwt: string, promptText: string) => {
  const response = await fetch("/api/favorites", {
    method: "post",
    headers: { "Content-Type": "application/json", "Authorization": jwt },
    body: JSON.stringify({ prompt_text: promptText })
  });
  if (!response.ok) {
    reloginOn401(response);
    throw new Error(`Failed with status ${response.status}`);
  }
  return response.json();
};

const deleteFavorite = async (jwt: string, favoriteId: string) => {
  const response = await fetch(`/api/favorites/${favoriteId}`, {
    method: "delete",
    headers: { "Content-Type": "application/json", "Authorization": jwt }
  });
  if (!response.ok) {
    reloginOn401(response);
    throw new Error(`Failed with status ${response.status}`);
  }
  return response.json();
};

// Biblioteca de artifacts del usuario (sección "Artifacts" del panel
// lateral): uno por conversación, sin HTML. El HTML de la versión que
// se abre se trae aparte con fetchArtifactById.
const fetchArtifacts = async (jwt: string) => {
  const response = await fetch("/api/artifacts", {
    method: "get",
    headers: { "Content-Type": "application/json", "Authorization": jwt }
  });
  if (!response.ok) {
    reloginOn401(response);
    throw new Error(`Failed with status ${response.status}`);
  }
  return response.json();
};

const fetchArtifactById = async (jwt: string, artifactId: string) => {
  const response = await fetch(`/api/artifact/${artifactId}`, {
    method: "get",
    headers: { "Content-Type": "application/json", "Authorization": jwt }
  });
  if (!response.ok) {
    reloginOn401(response);
    throw new Error(`Failed with status ${response.status}`);
  }
  return response.json();
};

const getUserFeedback = async (jwt: string, userId: string) => {
  const response = await fetch(`/api/feedback/${encodeURIComponent(userId)}`, {
    method: "get",
    headers: { "Content-Type": "application/json", "Authorization": jwt },
    targetAddressSpace: 'private'
  });
  if (!response.ok) {
    if (response.status === 404) return null;
    reloginOn401(response);
    throw new Error(`Failed with status ${response.status}`);
  }
  return response.json();
};

const saveUserFeedback = async (jwt: string, userId: string, rating: number) => {
  const response = await fetch(`/api/feedback`, {
    method: "post",
    headers: { "Content-Type": "application/json", "Authorization": jwt },
    body: JSON.stringify({ user_id: userId, rating }),
    targetAddressSpace: 'private'
  });
  if (!response.ok) {
    reloginOn401(response);
    throw new Error(`Failed with status ${response.status}`);
  }
  return response.json();
};

export default { fetchToken, fetchTokenFromTeams, fetchTokenFromAAD, fetchConversationsIds, fetchConversationHistory, fetchPromptsTotry, fetchPromptsHistory, fetchFavorites, saveFavorite, deleteFavorite, fetchArtifacts, fetchArtifactById, getUserFeedback, saveUserFeedback }
